import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import { ClickAwayListener, Grow, Link, MenuItem, Paper, Popper, Tab } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CustomAutocomplete from '../../../components/CustomAutocomplete';
import CustomSearch from '../../../components/CustomSearch';
import CustomSelect from '../../../components/CustomSelect';
import CustomSplitButton from '../../../components/CustomSplitButton';
import { ControlpanelSuccess3thButton } from '../../../components/StyledButton';
import { CustomAdvancedMenuList, CustomTabsV2 } from '../../../components/StyledComponents';
import { getListUserGroup } from '../../../axios/Group/GroupUrlApi';
import { useAuth } from '../../../context/AuthContext';
import { LoadingProvider } from '../../../context/LoadingContext';
import HolidaysGridInfoTable from './HolidaysGridInfoTable';
import LeaveAndHolidaysMonthEventsCalendar from './LeaveAndHolidaysMonthEventsCalendar';
import LeaveAndHolidayChart from './LeaveAndHolidayChart';
import LeavesGridInfoTable from './LeavesGridInfoTable';
import ImportIcon16pxSvg from '../../../assets/images/icons/ImportIcon16pxSvg';
import ExportIcon16pxSvg from '../../../assets/images/icons/ExportIcon16pxSvg';

const CustomTabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} id={`leave-and-holidays-${index}`}>
    {value === index && <div className="mt-3">{children}</div>}
  </div>
);

const a11yProps = (index) => ({
  id: `leave-and-holidays-tab-${index}`,
  'aria-controls': `leave-and-holidays-tabpanel-${index}`,
});

const LeaveAndHolidayTabs = ({
  tabValue,
  setTabValue,
  selectedDate,
  setSelectedDate,
  refreshData,
  formatLeaveFilter,
  onFilterChange,
  groupData,
  dispalyWeek,
  countData,
  leaveTypesData,
  dialogRef,
  calculateDisplayedWeeks,
  setLoading,
  holidayPage,
  holidayPageSize,
  setHolidayPage,
  setHolidayPageSize,
  onHolidayRowSelection,
}) => {
  const { t } = useTranslation();
  const { isAdmin, genaralSettingDateFormat } = useAuth();
  const [showSummary, setShowSummary] = useState(true);
  const [, setTotalUserLeave] = useState(0);
  const [, setPendingRequestCount] = useState(0);
  const [userGroup, setUserGroup] = useState([]);
  const [holidaySearch, setHolidaySearch] = useState('');

  const [formData, setFormData] = useState({
    group: groupData[0]?.id ?? 1,
    selecStatusLeave: 0,
    userId: '',
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(formData.group ?? 1);

  const anchorRefAddLeave = useRef(null);
  const anchorRefAddHoliday = useRef(null);
  const [openAddLeaveMenu, setOpenAddLeaveMenu] = useState(false);
  const [openAddHolidayMenu, setOpenAddHolidayMenu] = useState(false);

  const statusLeaveData = [
    { statusLeaveId: 0, statusName: t('common.all-status') },
    { statusLeaveId: 1, statusName: t('common.approved') },
    { statusLeaveId: 2, statusName: t('common.rejected') },
    { statusLeaveId: 3, statusName: t('common.requested') },
  ];

  useEffect(() => {
    onFilterChange?.(formData);
  }, [formData, onFilterChange]);

  useEffect(() => {
    if (groupData?.length && formData.group === 1 && groupData[0]?.id) {
      setFormData((prev) => ({ ...prev, group: groupData[0].id }));
      setSelectedGroup(groupData[0].id);
    }
  }, [groupData, formData.group]);

  useEffect(() => {
    const fetchUsers = async () => {
      const payload = {
        group: selectedGroup,
        pageNo: 1,
        recordPerPage: 50000,
        langId: localStorage.getItem('i18nextLngId'),
      };
      try {
        const response = await getListUserGroup(payload);
        setUserGroup(response.data.data || []);
      } catch (error) {
        if (error.response?.status !== 401) {
          toast.error(t(error?.response?.data.message) || t('common.network-timeout'));
        }
      }
    };
    if (isAdmin) fetchUsers();
  }, [selectedGroup, isAdmin, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'group') setSelectedGroup(value);
  };

  const handleDataChange = (name, newData) => {
    setFormData((prev) => ({ ...prev, [name]: newData }));
    if (name === 'userId') setSelectedUser(newData);
  };

  const handleChangeTab = (_, newValue) => setTabValue(newValue);

  const handleCloseAddLeaveMenu = (event) => {
    if (anchorRefAddLeave.current?.contains(event.target)) return;
    setOpenAddLeaveMenu(false);
  };

  const handleCloseAddHolidayMenu = (event) => {
    if (anchorRefAddHoliday.current?.contains(event.target)) return;
    setOpenAddHolidayMenu(false);
  };

  const renderImportExportMenu = (open, anchorRef, onClose, type) => (
    <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" transition disablePortal className="z-10">
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{ transformOrigin: placement === 'bottom' ? 'right top' : 'right bottom' }}
        >
          <Paper style={{ borderRadius: '6px' }} className="shadow-primary mt-1">
            <ClickAwayListener onClickAway={onClose}>
              <CustomAdvancedMenuList>
                <MenuItem
                  style={{
                    borderBottom: "1px solid #E5E7EB",
                    opacity: "0.5",
                    pointerEvents: "none",
                  }}
                  onClick={(e) => {
                    dialogRef?.current?.openSelectDataToImport?.();
                    onClose(e);
                  }}
                >
                  <Link>
                    <ImportIcon16pxSvg />
                    <p>{t('button.import-data')}</p>
                  </Link>
                </MenuItem>
                <MenuItem
                  style={{
                    borderBottom: "1px solid #E5E7EB",
                    opacity: "0.5",
                    pointerEvents: "none",
                  }}
                  onClick={(e) => {
                    dialogRef?.current?.openSelectDataToExport?.();
                    onClose(e);
                  }}
                >
                  <Link>
                    <ExportIcon16pxSvg />
                    <p>{t('button.export-data')}</p>
                  </Link>
                </MenuItem>
              </CustomAdvancedMenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );

  const renderLeaveRequestHeader = () => (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 w-full px-4 mt-1 py-2">
      <div className="flex flex-1 min-w-0 flex-wrap lg:flex-nowrap items-stretch gap-3">
        {isAdmin && (
          <CustomAutocomplete
            className="w-full min-w-0 flex-1 basis-[180px]"
            name="userId"
            value={selectedUser}
            onDataChange={handleDataChange}
            options={userGroup}
            dataType="user"
            freeSolo
            placeholder={t('common.content-161')}
          />
        )}
        {isAdmin && (
          <CustomSelect
            name="group"
            value={formData.group}
            onChange={handleInputChange}
            options={groupData}
            dataType="group"
            className="w-full min-w-0 flex-1 basis-[180px]"
          />
        )}
        {tabValue === 1 && (
          <CustomSelect
            name="selecStatusLeave"
            value={formData.selecStatusLeave}
            onChange={handleInputChange}
            options={statusLeaveData}
            dataType="selecStatusLeave"
            className="w-full min-w-0 flex-1 basis-[180px]"
          />
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0 flex-nowrap justify-end">
        {isAdmin && (
          <ControlpanelSuccess3thButton onClick={() => dialogRef?.current?.openConfigureLeaveTypes?.()}>
            <SettingsIcon />
            <p className="ml-1 whitespace-nowrap">{t('button.configure-leave-types')}</p>
          </ControlpanelSuccess3thButton>
        )}
        {(
          <div className="relative shrink-0">
            <CustomSplitButton
              label={t('button.add-leave')}
              startIcon={<AddIcon className="!text-[16px]" />}
              onMainClick={() => dialogRef?.current?.openAddLeave?.()}
              onDropdownClick={() => setOpenAddLeaveMenu((prev) => !prev)}
              ref={anchorRefAddLeave}
            />
            {renderImportExportMenu(openAddLeaveMenu, anchorRefAddLeave, handleCloseAddLeaveMenu, 'leave')}
          </div>
        )}
        {/* {tabValue === 1 && (
          <ControlpanelPrimaryButton onClick={() => dialogRef?.current?.openAddLeaveRequest?.()}>
            <AddIcon />
            <p className="ml-1 whitespace-nowrap">{t('button.add-request')}</p>
          </ControlpanelPrimaryButton>
        )} */}
      </div>
    </div>
  );

  const renderHolidayHeader = () => (
    <div className="flex flex-col items-start lg:flex-row lg:items-center gap-3 w-full mt-1 px-4 pb-2">
      <CustomSearch
        className="w-full lg:flex-1 lg:min-w-0"
        onSearchChange={setHolidaySearch}
      />
      {isAdmin && (
        <div className="relative w-fit max-w-full shrink-0">
          <CustomSplitButton
            label={t('button.add-holiday')}
            startIcon={<AddIcon className="!text-[16px]" />}
            onMainClick={() => dialogRef?.current?.openAddHoliday?.()}
            onDropdownClick={() => setOpenAddHolidayMenu((prev) => !prev)}
            ref={anchorRefAddHoliday}
          />
          {renderImportExportMenu(openAddHolidayMenu, anchorRefAddHoliday, handleCloseAddHolidayMenu, 'holiday')}
        </div>
      )}
    </div>
  );

  const renderLeaveRequestSummary = () => (
    <>
      <div
        className="flex items-center justify-between bg-white rounded-xl px-3 sm:px-4 cursor-pointer px-4"
        onClick={() => setShowSummary((prev) => !prev)}
      >
        <span className="font-semibold text-base sm:text-lg md:text-[20px]">
          {t("common.summary")}
        </span>
        {showSummary ? <ExpandMoreIcon /> : <ExpandLessIcon />}
      </div>
      {showSummary && (
        <div className="grid grid-cols-1 3xl:grid-cols-12 gap-3 sm:gap-4 min-w-0 px-5 pt-2">
          <div className="col-span-1 3xl:col-span-9 min-w-0">
            <div className="card bg-white rounded-xl min-w-0 h-full">
              <LoadingProvider>
                <LeaveAndHolidaysMonthEventsCalendar
                  tabValue={tabValue}
                  setSelectedDate={setSelectedDate}
                  selectedDate={selectedDate}
                  setLoading={setLoading}
                  refreshData={refreshData}
                  calculateDisplayedWeeks={calculateDisplayedWeeks}
                  formatLeaveFilter={formatLeaveFilter}
                  selectedUser={selectedUser}
                  selectedGroup={selectedGroup}
                />
              </LoadingProvider>
            </div>
          </div>
          <div className="col-span-1 3xl:col-span-3 min-w-0">
            <div className="card bg-white rounded-xl flex justify-center items-center w-full min-w-0 h-full xl:ml-1">
              <LoadingProvider>
                <LeaveAndHolidayChart
                  key={tabValue === 0 ? 'leaves' : 'requests'}
                  chartType={tabValue === 0 ? 'leaves' : 'requests'}
                  selectedDate={selectedDate}
                  formatLeaveFilter={formatLeaveFilter}
                  refreshData={refreshData}
                />
              </LoadingProvider>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="leave-and-holiday-tab min-w-0 w-full max-w-full">
      <CustomTabsV2
        variant="fullWidth"
        scrollButtons="auto"
        allowScrollButtonsMobile
        indicatorColor="none"
        value={tabValue}
        onChange={handleChangeTab}
      >
        <Tab
          label={
            <div className="flex items-center gap-2">
              <p className="text-sm">{t('common.leaves')}</p>
            </div>
          }
          {...a11yProps(0)}
          disableRipple
        />
        <Tab
          label={
            <div className="flex items-center gap-2">
              <p className={`text-sm ${tabValue === 1 ? 'text-[#2563EB]' : ''}`}>{t('common.request1')}</p>
            </div>
          }
          {...a11yProps(1)}
          disableRipple
        />
        <Tab
          label={
            <div className="flex items-center gap-2">
              <p className="text-sm">{t('common.holidays')}</p>
            </div>
          }
          {...a11yProps(2)}
          disableRipple
        />
      </CustomTabsV2>

      {(tabValue === 0 || tabValue === 1) && renderLeaveRequestHeader()}

      {(tabValue === 0 || tabValue === 1) && renderLeaveRequestSummary()}

      <CustomTabPanel value={tabValue} index={0}>
        <div className="bg-white rounded-xl min-w-0 w-full px-4 py-1">
          <LoadingProvider>
            <LeavesGridInfoTable
              gridMode="leaves"
              selectedDate={selectedDate}
              refreshData={refreshData}
              formatLeaveFilter={formatLeaveFilter}
              dispalyWeek={dispalyWeek}
              genaralSettingDateFormat={genaralSettingDateFormat}
              leaveTypesData={leaveTypesData}
              setTotalUserLeave={setTotalUserLeave}
              handleGetCountData={() => { }}
            />
          </LoadingProvider>
        </div>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        <div className="bg-white rounded-xl min-w-0 w-full px-4 py-1">
          <LoadingProvider>
            <LeavesGridInfoTable
              gridMode="requests"
              selectedDate={selectedDate}
              refreshData={refreshData}
              formatLeaveFilter={formatLeaveFilter}
              dispalyWeek={dispalyWeek}
              genaralSettingDateFormat={genaralSettingDateFormat}
              leaveTypesData={leaveTypesData}
              setPendingRequestCount={setPendingRequestCount}
              handleGetCountData={() => { }}
            />
          </LoadingProvider>
        </div>
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={2}>
        {renderHolidayHeader()}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 sm:gap-4 min-w-0 px-4 py-2">
          <div className="col-span-1 xl:col-span-5 min-w-0">
            <div className="card bg-white rounded-xl min-w-0 p-2">
              <LoadingProvider>
                <LeaveAndHolidaysMonthEventsCalendar
                  tabValue={2}
                  setSelectedDate={setSelectedDate}
                  selectedDate={selectedDate}
                  setLoading={setLoading}
                  refreshData={refreshData}
                  calculateDisplayedWeeks={calculateDisplayedWeeks}
                  formatLeaveFilter={formatLeaveFilter}
                  selectedUser={selectedUser}
                  selectedGroup={selectedGroup}
                />
              </LoadingProvider>
            </div>
          </div>
          <div className="col-span-1 xl:col-span-7 min-w-0">
            <div className="bg-white rounded-xl min-w-0 w-full">
              <LoadingProvider>
                <HolidaysGridInfoTable
                  selectedDate={selectedDate}
                  page={holidayPage}
                  pageSize={holidayPageSize}
                  refreshData={refreshData}
                  setPage={setHolidayPage}
                  setPageSize={setHolidayPageSize}
                  setLoading={setLoading}
                  onRowSelection={onHolidayRowSelection}
                  handleGetCountData={() => { }}
                  countData={countData}
                  dispalyWeek={dispalyWeek}
                  genaralSettingDateFormat={genaralSettingDateFormat}
                  externalQuickFilter={holidaySearch}
                  hideToolbar
                  dialogRef={dialogRef}
                />
              </LoadingProvider>
            </div>
          </div>
        </div>
      </CustomTabPanel>
    </div>
  );
};

export default LeaveAndHolidayTabs;
